import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  subscribeStationNotifications,
  unsubscribeStationNotifications,
} from "../../lib/webPush";
import { useSession } from "../auth/hooks";

import { useFavorites, useToggleFavorite } from "./hooks";

type Props = {
  stationId: string;
};

export function HeartButton({ stationId }: Props) {
  const { t } = useTranslation();
  const { user, loading, error: sessionError } = useSession();
  const authed = !!user;
  const favoritesQuery = useFavorites(authed);
  const toggle = useToggleFavorite();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!errorMessage) return;
    const id = setTimeout(() => setErrorMessage(null), 4000);
    return () => clearTimeout(id);
  }, [errorMessage]);

  const favorited = !!favoritesQuery.data?.some((f) => f.stationId === stationId);
  const disabled = loading || sessionError || !authed || toggle.isPending;
  const label = favorited ? t("favorites.remove_from") : t("favorites.add_to");
  const tooltip = sessionError
    ? t("favorites.service_unavailable_short")
    : authed
      ? label
      : t("favorites.signin_to_save");

  function handleClick() {
    setErrorMessage(null);
    const adding = !favorited;
    toggle.mutate(
      { stationId, favorite: adding },
      {
        onSuccess: () => {
          // Fire-and-forget push registration — failures (denied permission,
          // missing VAPID key, unsupported browser, network) must not break
          // favoriting. Log so real network/auth errors are visible in console.
          const push = adding
            ? subscribeStationNotifications(stationId)
            : unsubscribeStationNotifications(stationId);
          void push.catch((err: unknown) => {
            console.warn("[webPush]", err);
          });
        },
        onError: (err) => {
          setErrorMessage(
            err instanceof Error
              ? t("favorites.save_failed_with_reason", { reason: err.message })
              : t("favorites.save_failed"),
          );
        },
      },
    );
  }

  return (
    <span className="relative inline-flex flex-col items-end">
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled}
        aria-pressed={favorited}
        aria-label={label}
        title={tooltip}
        className={`inline-flex h-8 w-8 items-center justify-center rounded-full transition ${
          disabled ? "cursor-not-allowed opacity-50" : "hover:bg-ember/5"
        }`}
      >
        <svg
          viewBox="0 0 24 24"
          width="20"
          height="20"
          aria-hidden="true"
          className={favorited ? "fill-rose-600 stroke-rose-600" : "fill-none stroke-current"}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 1 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
        </svg>
      </button>
      {errorMessage && (
        <span
          role="alert"
          aria-live="polite"
          className="absolute right-0 top-9 whitespace-nowrap rounded-md bg-ember/15 px-2 py-1 text-xs text-ember shadow-sm"
        >
          {errorMessage}
        </span>
      )}
    </span>
  );
}
